import React, { useState } from "react"
import PDFViewer from "./PdfViewer"
import MatchScoreBar from "./MatchScoreBar"
import { MatchResult } from "../apis/api"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import HorizontalBarChart from "./HorizontalBarChart"
import * as localStorageManager from "../utils/localStorageManager"

interface TabProps {
  isActive: boolean;
  onClick: () => void;
  label: string;
}

const Tab: React.FC<TabProps> = ({ isActive, onClick, label }) => {
  return (
    <button
      onClick={onClick}
      className={`
        px-4 py-2 text-sm font-medium border-b-2 transition-colors duration-200 
        ${isActive 
          ? 'border-blue-600 text-blue-600' 
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}
      `}
    >
      {label}
    </button>
  );
}

interface SummaryDisplayProps {
  fileContent: string | null
  summary: string | null
  isGenerating: boolean
  isMatching?: boolean
  onGenerateSummary: () => void
  onMatchWithCV?: () => void
  pdfUrl: string | null
  layout?: "vertical" | "horizontal"
  matchResult?: MatchResult | null
}

const SummaryDisplay: React.FC<SummaryDisplayProps> = ({
  fileContent,
  summary,
  isGenerating,
  isMatching = false,
  onGenerateSummary,
  onMatchWithCV,
  pdfUrl,
  layout = "vertical",
  matchResult
}) => {
  // Initialize tab state based on available data
  const getInitialTab = (): 'summary' | 'match' => 
  matchResult ? 'match' : 'summary';

  const [activeTab, setActiveTab] = useState<'summary' | 'match'>(getInitialTab);

  // Function to check if both jd_id and cv_id exist in local storage
  const canMatchWithJobDescription = () => {
    const jdId = localStorageManager.getJobDescriptionId();
    const cvId = localStorageManager.getFileId();
    return Boolean(jdId && cvId);
  };

  return (
    <div className="mt-6 pb-10">
      {/* Container with flex layout */}
      <div
        className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden
                      ${layout === "horizontal" ? "flex flex-col md:flex-row" : ""}`}
      >
        {/* PDF Viewer Section - Left Side */}
        {pdfUrl ? (
          <div
            className={`${layout === "horizontal" ? "md:w-3/5 border-r border-gray-200" : "w-full"}`}
          >
            <div className="p-2 bg-gray-50 border-b border-gray-200">
              <h4 className="font-medium">Resume Preview</h4>
              {/* {pdfUrl && pdfUrl.startsWith('http') && (
                <a 
                  href={pdfUrl} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="text-xs text-blue-600 hover:text-blue-800 ml-2"
                >
                  Open in new tab
                </a>
              )} */}
            </div>
            <div className="flex flex-col">
              <div className="relative">
                <PDFViewer pdfUrl={pdfUrl} />
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No PDF preview available
          </div>
        )}

        {/* Summary Section with Tabs - Right Side */}
        <div
          className={`${layout === "horizontal" && pdfUrl ? "md:w-2/5" : "w-full"} overflow-y-auto flex flex-col`}
        >
          {/* Tab Navigation */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="flex">
              <Tab
                isActive={activeTab === 'summary'}
                onClick={() => setActiveTab('summary')}
                label="Generate Summary"
              />
              <Tab 
                isActive={activeTab === 'match'}
                onClick={() => setActiveTab('match')}
                label="Match with Job Description"
              />
            </div>
          </div>

          <div className="p-4">
            {fileContent && (
              <div className="mb-4">
                <h4 className="text-base font-medium text-gray-700 mb-1">Parsed Content:</h4>
                <div className="bg-gray-50 p-3 rounded border border-gray-200 max-h-60 overflow-auto">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">{fileContent}</pre>
                </div>
              </div>
            )}

            {/* Summary Tab Content */}
            {activeTab === 'summary' && (
              <div>
                {!isGenerating ? (
                  <>
                    
                    <button
                      onClick={onGenerateSummary}
                      className="bg-blue-500 text-white px-3 py-1.5 text-sm rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                      disabled={isGenerating}
                    >
                      {summary ? "Regenerate Summary" : "Generate Summary"}
                    </button>
                  </>
                ) : (
                  <div className="flex items-center text-gray-600 text-sm">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Generating summary...
                  </div>
                )}

                {summary && (
                  <div className="prose max-w-none mt-4">
                    <h4 className="text-base font-medium text-gray-700 mb-1">Summary:</h4>
                    <div className="bg-gray-50 p-3 rounded border border-gray-200">
                      <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-gray-800 prose-p:text-gray-700 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-code:bg-gray-100 prose-code:text-gray-800 prose-code:rounded prose-code:px-1 prose-pre:bg-gray-800 prose-pre:text-gray-100 prose-p:my-1 prose-headings:my-2 prose-li:my-0.5 prose-ul:my-1 prose-ol:my-1 prose-pre:my-2 text-sm [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-sm [&_h4]:text-sm [&_h5]:text-sm [&_h6]:text-sm [&_p]:text-sm [&_li]:text-sm [&_code]:text-sm">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {summary}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Match with Job Description Tab Content */}
            {activeTab === 'match' && (
              <div>
                {!isMatching ? (
                  <>
                    {!canMatchWithJobDescription() && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                        <p className="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          You need both a resume and job description to match. 
                          {!localStorageManager.getFileId() && " Please upload a resume."}
                          {!localStorageManager.getJobDescriptionId() && " Please add a job description."}
                        </p>
                      </div>
                    )}
                    {onMatchWithCV && (
                      <button
                        onClick={onMatchWithCV}
                        className={`px-3 py-1.5 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
                          canMatchWithJobDescription() 
                            ? "bg-blue-500 text-white hover:bg-blue-600" 
                            : "bg-blue-300 text-white cursor-not-allowed"
                        }`}
                        disabled={isMatching || !canMatchWithJobDescription()}
                        title={!canMatchWithJobDescription() ? "Both resume and job description are required" : ""}
                      >
                        {matchResult ? "Match Again" : "Match with Job Description"}
                      </button>
                    )}
                  </>
                ) : (
                  <div className="flex items-center text-gray-600 text-sm">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-green-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Analyzing match with job description...
                  </div>
                )}
                
                {/* Match Results Section */}
                {matchResult && (
                  <div className="mt-4">
                    {/* <h4 className="text-base font-medium text-gray-700 mb-1">Job Description Match:</h4> */}
                    <div className="mb-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-sm">Candidate-Job Fit Score:</span>
                        <span className="font-bold text-lg">{Math.round(matchResult.score)}%</span>
                      </div>
                      <MatchScoreBar score={matchResult.score} />
                    </div>
                    
                    {matchResult.details && Object.keys(matchResult.details).length > 0 && (
                      <div className="mt-3 bg-gray-50 p-3 rounded border border-gray-200">
                        {/* <h5 className="font-medium text-sm mb-3">Match Details:</h5> */}
                        
                        <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-gray-800 prose-p:text-gray-700 text-sm">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {`${matchResult.details.summary ? `#### Summary\n${matchResult.details.summary}\n\n` : ''}${matchResult.details.relevantKeywords ? 
                              `#### Relevant Keywords\n${Array.isArray(matchResult.details.relevantKeywords) ? 
                                matchResult.details.relevantKeywords.join(', ') : 
                                String(matchResult.details.relevantKeywords)}\n\n` : ''}${matchResult.details.missingKeywords ? 
                              `#### Missing Keywords\n${Array.isArray(matchResult.details.missingKeywords) ? 
                                matchResult.details.missingKeywords.join(', ') : 
                                String(matchResult.details.missingKeywords)}` : ''}`}
                          </ReactMarkdown>
                        </div>
                        
                        <div className="mt-4">
                          <h6 className="text-sm font-medium mb-2">Score Breakdown:</h6>
                          <HorizontalBarChart
                            data={[
                              {
                                label: 'Resume Job Fit Score',
                                value: typeof matchResult.details.atsScore === 'number' ? 
                                  matchResult.details.atsScore : 
                                  parseInt(String(matchResult.details.atsScore || '0')),
                                color: 'bg-blue-300'
                              },
                              {
                                label: 'Keyword Matching',
                                value: typeof matchResult.details.keywordMatchingPercentage === 'number' ? 
                                  matchResult.details.keywordMatchingPercentage : 
                                  parseInt(String(matchResult.details.keywordMatchingPercentage || '0')),
                                color: 'bg-green-300'
                              },
                              {
                                label: 'Years of Experience',
                                value: typeof matchResult.details.yearsOfExperiencePercentage === 'number' ? 
                                  matchResult.details.yearsOfExperiencePercentage : 
                                  parseInt(String(matchResult.details.yearsOfExperiencePercentage || '0')),
                                color: 'bg-amber-300'
                              },
                              {
                                label: 'Project Relevance',
                                value: typeof matchResult.details.projectRelevancePercentage === 'number' ? 
                                  matchResult.details.projectRelevancePercentage : 
                                  parseInt(String(matchResult.details.projectRelevancePercentage || '0')),
                                color: 'bg-violet-300'
                              },
                              {
                                label: 'Quantifiable Achievements',
                                value: typeof matchResult.details.quantifiableAchievementsPercentage === 'number' ? 
                                  matchResult.details.quantifiableAchievementsPercentage : 
                                  parseInt(String(matchResult.details.quantifiableAchievementsPercentage || '0')),
                                color: 'bg-rose-300'
                              }
                            ]}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SummaryDisplay
